import { Reducer } from "react";
import {
  getNewPage,
  getNewWidgetPages,
  getPageIndex,
  MAX_PAGES,
  moveWidget,
  moveWidgetToEmptySpot,
  removeWidget,
} from "./utils";
import { FAKE_WIDGET_ID } from "@services/widgets/utils";
import { FiltersTypeImpl } from "@common/utils/filters";

const handleSimpleActions: Reducer<WidgetContextEntity, SimpleActions> = (
  state,
  action
) => {
  switch (action) {
    case "toggleEdit":
      return { ...state, editMode: !state.editMode };
    case "newPage":
      return {
        ...state,
        pages: [
          ...state.pages,
          {
            ...getNewPage(),
            screenOrder: state.pages.length,
          },
        ],
      };
    case "defaultPage":
      const newPage = getNewPage();
      newPage.widgets = [
        {
          label: "Devices",
          userWidgetId: FAKE_WIDGET_ID,
          filters: [
            {
              filterType: "status",
              filterValue: "All",
            },
          ],
          widgetType: FiltersTypeImpl.Devices,
        },
        {
          label: "Devices",
          userWidgetId: FAKE_WIDGET_ID,
          filters: [
            {
              filterType: "status",
              filterValue: "Up",
            },
          ],
          widgetType: FiltersTypeImpl.Devices,
        },
        {
          label: "Devices",
          userWidgetId: FAKE_WIDGET_ID,
          filters: [
            {
              filterType: "status",
              filterValue: "Unreachable",
            },
          ],
          widgetType: FiltersTypeImpl.Devices,
        },
      ];
      return {
        ...state,
        pages: [{ ...newPage, screenOrder: 0 }],
      };
    case "nextPage":
      return {
        ...state,
        currentPage:
          state.currentPage +
          (state.currentPage >= state.pages.length - 1 ? 0 : 1),
      };
    case "previousPage":
      return {
        ...state,
        currentPage: state.currentPage - (state.currentPage === 0 ? 0 : 1),
      };
    case "carousel":
      if (!state.canCarousel || state.editMode || !state.isCarouselEnabled)
        return { ...state };
      return {
        ...state,
        currentPage:
          state.currentPage >= state.pages.length - 1
            ? 0
            : state.currentPage + 1,
      };
    case "temporaryEnableCarousel":
      return { ...state, canCarousel: true };
    case "temporaryDisableCarousel":
      return { ...state, canCarousel: false };
    case "disableCarousel":
      return { ...state, isCarouselEnabled: false };
    case "enableCarousel":
      return { ...state, isCarouselEnabled: true };
    default:
      return state;
  }
};

export const reducer: Reducer<WidgetContextEntity, Actions> = (
  state,
  action
) => {
  if (typeof action === "string") return handleSimpleActions(state, action);
  switch (action.type) {
    case "onDrag":
      return {
        ...state,
        isDragging: action.data.isDragging ? action.data.userWidgetId : false,
      };
    case "widgetHeight":
      return { ...state, widgetHeight: action.data };
    case "newWidget":
      return newWidgetReducer(state, action);
    case "moveWidget":
      return moveWidgetReducer(state, action);
    case "moveWidgetToEmptySpot":
      return moveWidgetToEmptySpotReducer(state, action);
    case "removeWidget":
      return { ...state, pages: removeWidget(action.data, state) };
    case "changePage":
      return { ...state, canCarousel: false, currentPage: action.data };
    case "updateWidget":
      const { userWidgetId, ...other } = action.data;
      return {
        ...state,
        pages: state.pages.map((page) => {
          return {
            ...page,
            widgets: page.widgets.map((widget) => {
              if (widget.userWidgetId !== userWidgetId) return widget;
              return { ...widget, ...other };
            }),
          };
        }),
      };
    case "updatePages":
      return { ...state, pages: action.data };
    case "updatePage":
      const { page = state.currentPage, data } = action;
      return {
        ...state,
        pages: [
          ...state.pages.map((p, index) =>
            index === page ? { ...p, ...data } : p
          ),
        ],
      };
    default:
      return state;
  }
};
export default reducer;
const newWidgetReducer = (
  state: WidgetContextEntity,
  { data: widget }: GlobalAction<"newWidget", UserWidgetCreation>
): WidgetContextEntity => {
  const pages = getNewWidgetPages(state, widget);
  return {
    ...state,
    currentPage: getPageIndex(pages, {
      userWidgetId: FAKE_WIDGET_ID,
    })[0],
    pages,
  };
};

const moveWidgetReducer = (
  state: WidgetContextEntity,
  action: GlobalAction<
    "moveWidget",
    UserWidget,
    {
      target: UserWidget;
      callback?: (newPages: UserWidgetScreen[]) => void;
      onError?: (pages: UserWidgetScreen[]) => void;
    }
  >
) => {
  const pages = moveWidget(state.pages, action);
  if (pages.length > MAX_PAGES) {
    (action.onError ?? (() => {}))(pages);
    return state;
  }
  if (action.callback) action.callback(pages);
  return {
    ...state,
    pages: [...pages],
    currentPage: Math.min(state.currentPage, pages.length - 1),
  };
};
const moveWidgetToEmptySpotReducer = (
  state: WidgetContextEntity,
  action: GlobalAction<
    "moveWidgetToEmptySpot",
    UserWidget,
    {
      callback?: (newPages: UserWidgetScreen[]) => void;
      onError?: (pages: UserWidgetScreen[]) => void;
    }
  >
): WidgetContextEntity => {
  const pages = moveWidgetToEmptySpot(state, action);
  if (pages.length > MAX_PAGES) {
    (action.onError ?? (() => {}))(pages);
    return state;
  }
  if (action.callback) action.callback(pages);
  return {
    ...state,
    pages,
    currentPage: Math.min(state.currentPage, pages.length - 1),
  };
};
